import { computed, reactive, readonly, Ref, ref } from 'vue'

import { SHORT_WORD_EXTRA_SENSITIVITY_LENGTH, SHORT_WORD_SENSITIVITY_MULTIPLIER } from '@/constants/audioConstants'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { RecorderService } from '@/util/RecorderService'
import { RecorderServiceOptions } from '@/util/Recording'
import { regularStyle, userSpeakingStyle } from '@/util/TextStyles'

function useFollower(recorderService: Ref<RecorderService | null>) {
  const currentStyle: Ref<string> = ref('')
  const currentSentenceIndex: Ref<number> = ref(-1)
  const currentSegmentIndex: Ref<number> = ref(-1)
  const currentElement: Ref<HTMLElement | null> = ref(null)
  const baseLevel: Ref<number | null> = ref(null)
  const triggerLevel: Ref<number | null> = ref(null)
  const betweenVoiceStartAndStop: Ref<boolean> = ref(false)

  function followerSegmentStyle(style: string, _: AnnotatedSegment, sentenceIndex: number, segmentIndex: number): string {
    if (sentenceIndex === currentSentenceIndex.value && segmentIndex === currentSegmentIndex.value) {
      return currentStyle.value
    }
    return style
  }

  function setCurrentSegment(sentenceIndex: number, segmentIndex: number, annotatedSegment: AnnotatedSegment | null = null): void {
    currentSentenceIndex.value = sentenceIndex
    currentSegmentIndex.value = segmentIndex

    // make the sound EMA more sensitive for short words
    if (annotatedSegment && annotatedSegment.nChars() <= SHORT_WORD_EXTRA_SENSITIVITY_LENGTH) {
      if (recorderService.value) {
        recorderService.value.setSoundEwmaHalfLifeMultiplier(SHORT_WORD_SENSITIVITY_MULTIPLIER)
      }
    }
  }

  const followerOptions = reactive<RecorderServiceOptions>({
    audioFilters: false,
    onBaseLevel: (bl: number, _perc: number) => {
      baseLevel.value = bl
    },

    onError: (_: Error) => {},

    onRecordingReady: async (_: Event) => {},

    // nop — alpha-based segment highlighting removed
    onSoundUpdate: (_: number) => {},

    onStart: () => {},

    onStartRecording: () => {
      setCurrentSegment(currentSentenceIndex.value, currentSegmentIndex.value, null)
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onStartSilence: () => {},

    onStopRecording: () => {
      currentStyle.value = regularStyle
    },

    onTriggerLevel: (tl: number) => {
      triggerLevel.value = tl
    },

    onVoiceStart: (elementId: string) => {
      betweenVoiceStartAndStop.value = true
      currentElement.value = document.getElementById(elementId)
      currentStyle.value = userSpeakingStyle
    },

    onVoiceStop: (manualSegmentation = false) => {
      return new Promise<void>((resolve) => {
        betweenVoiceStartAndStop.value = false
        currentStyle.value = regularStyle
        if (!manualSegmentation) {
          currentElement.value = null
        }
        resolve()
      })
    },

    voiceActivityDetection: true,
  } as RecorderServiceOptions)

  return {
    baseLevel: computed({
      get: () => baseLevel.value,
      set: (v: number | null) => {
        baseLevel.value = v
      },
    }),
    betweenVoiceStartAndStop: computed(() => betweenVoiceStartAndStop.value),
    currentElement: computed(() => currentElement.value),
    currentSegmentIndex: computed(() => currentSegmentIndex.value),
    currentSentenceIndex: computed(() => currentSentenceIndex.value),
    currentStyle: computed({
      get: () => currentStyle.value,
      set: (v: string) => {
        currentStyle.value = v
      },
    }),
    followerOptions: readonly(followerOptions),
    followerSegmentStyle,
    setCurrentSegment,
    triggerLevel: computed(() => triggerLevel.value),
  }
}

export { useFollower }
